import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { API, apiUrl } from './routes';
import { refreshAccessToken } from './api';
import { AppAbility, buildAbility, createDefaultAbility } from './AbilityContext';

interface Territory {
  id: string;
  region: string | null;
  zone: string;
  zoneName: string;
  isPrimary: boolean;
}

interface DataScope {
  scopeType: string;
  scopeValue: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: string;
  siteRole?: string;
  authProvider?: string;
  territories: Territory[];
  dataScopes?: DataScope[];
  hasDirectReports?: boolean;
  directReportsCount?: number;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  ability: AppAbility;
  currentRegion: string;
  currentTerritory: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => void;
  switchRegion: (region: string) => void;
  switchTerritory: (zone: string) => void;
  handleApiError: (response: Response) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Global 401 handler - services can call this without needing React context
let globalLogoutHandler: (() => void) | null = null;

export function triggerLogout() {
  if (globalLogoutHandler) {
    globalLogoutHandler();
  }
}

export function handleApiResponse(response: Response): Response {
  if (response.status === 401) {
    console.warn('Session expired or invalid, logging out...');
    triggerLogout();
  }
  return response;
}

// Dev mode bypass - set VITE_DEV_MODE=true to skip authentication
const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true';

const MOCK_USER: User = {
  id: 'dev-user',
  email: 'dev@example.com',
  name: 'Dev User',
  role: 'ADMIN',
  siteRole: 'ADMIN',
  territories: [
    { id: '1', region: 'central-texas', zone: 'A', zoneName: 'North', isPrimary: true },
    { id: '2', region: 'central-texas', zone: 'B', zoneName: 'South', isPrimary: false }
  ],
  hasDirectReports: true,
  directReportsCount: 3,
};

// Dev mode admin rules - full access to everything
const DEV_ADMIN_RULES = [
  { action: 'manage', subject: 'all' },
  { action: 'view', subject: 'Settings' },
  { action: 'update', subject: 'Settings' },
  { action: 'view', subject: 'Account' },
  { action: 'create', subject: 'Account' },
  { action: 'update', subject: 'Account' },
  { action: 'delete', subject: 'Account' },
  { action: 'view', subject: 'Activity' },
  { action: 'create', subject: 'Activity' },
  { action: 'view', subject: 'Report' },
  { action: 'export', subject: 'Report' },
  { action: 'view', subject: 'User' },
  { action: 'update', subject: 'User' },
  { action: 'view', subject: 'Organization' },
  { action: 'manage', subject: 'Organization' },
];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(DEV_MODE ? MOCK_USER : null);
  // Token is kept for backward compat but not stored - auth uses httpOnly cookies
  const [token, setToken] = useState<string | null>(DEV_MODE ? 'dev-token' : null);
  const [ability, setAbility] = useState<AppAbility>(() =>
    DEV_MODE ? buildAbility(DEV_ADMIN_RULES) : createDefaultAbility()
  );
  const [currentRegion, setCurrentRegion] = useState<string>('all');
  const [currentTerritory, setCurrentTerritory] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(!DEV_MODE);

  const logout = useCallback(async () => {
    // Call backend to clear httpOnly cookies
    try {
      await fetch(apiUrl(API.AUTH.LOGOUT), {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors during logout
    }

    /* Deliberate sign-out must also drop the offline session cache, or a
       signed-out rep relaunching in a dead zone would be restored. */
    try { localStorage.removeItem('rv_auth_user_cache'); } catch { /* noop */ }
    setUser(null);
    setToken(null);
    setAbility(createDefaultAbility());
    setCurrentRegion('all');
    setCurrentTerritory('all');
    localStorage.removeItem('territory_crm_current_region');
    localStorage.removeItem('territory_crm_current_territory');
  }, []);

  // Register global logout handler
  useEffect(() => {
    globalLogoutHandler = logout;
    return () => {
      globalLogoutHandler = null;
    };
  }, [logout]);

  // Handle API errors - returns true if the error was handled (401 = logout)
  const handleApiError = useCallback((response: Response): boolean => {
    if (response.status === 401) {
      console.warn('Session expired or invalid, logging out...');
      logout();
      return true;
    }
    return false;
  }, [logout]);

  const fetchAbilities = useCallback(async () => {
    try {
      const response = await fetch(apiUrl(API.USERS.ABILITIES), {
        credentials: 'include',
      });

      if (response.ok) {
        const { rules } = await response.json();
        const newAbility = buildAbility(rules);
        setAbility(newAbility);
      }
    } catch (error) {
      console.error('Failed to fetch abilities:', error);
    }
  }, []);

  // Check auth status via httpOnly cookies. CRITICAL · this is the gate the
  // wedge AuthGate uses to decide "logged in or not". The original raw fetch
  // here did NOT retry on 401, so a 15-min access_token expiry mid-session
  // would cascade: /auth/me 401 → isAuthenticated=false → WedgeAuthGate
  // bounces to OAuth → AuthCallbackWedge sees !isAuthenticated → redirects
  // to /login?error=denied → rep stares at AccessDenied screen for ~10s
  // before the refresh token finally settles things. The fix: when /auth/me
  // returns 401, attempt ONE refresh through sharedRefresh, then retry. If
  // the retry succeeds, the rep never sees the bounce. If both fail, the
  // session is genuinely dead and the bounce is correct.
  const checkAuthStatus = useCallback(async () => {
    const fetchMe = () => fetch(apiUrl(API.AUTH.ME), { credentials: 'include' });
    /* True once ANY HTTP response arrived · the offline-restore branch below
       must only run when the network itself failed, and a later parse error
       inside the success path must not masquerade as "offline". */
    let sawResponse = false;
    try {
      let response = await fetchMe();
      sawResponse = true;

      if (response.status === 401) {
        const refreshed = await refreshAccessToken().catch(() => false);
        if (refreshed) {
          response = await fetchMe();
        }
      }

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken('authenticated'); // Indicator that we're authed (actual token is in httpOnly cookie)
        /* 2026-07-27 · offline resilience. Cache the authed identity so a
           NETWORK-dead launch (rep in a signal dead zone - the app's core
           moment) can restore the session instead of dumping them on the
           login screen. Only ever read on a network failure, never on a 401:
           a genuine sign-out/revocation still lands on login, and the server
           re-checks auth on every API call regardless. */
        try { localStorage.setItem('rv_auth_user_cache', JSON.stringify({ u: userData, at: Date.now() })); } catch { /* quota · non-fatal */ }

        await fetchAbilities();

        if (userData.territories.length > 0) {
          const primary = userData.territories.find((t: Territory) => t.isPrimary);
          const defaultTerritory = primary?.zone || userData.territories[0].zone;
          const storedTerritory = localStorage.getItem('territory_crm_current_territory');

          if (!storedTerritory) {
            setCurrentTerritory(defaultTerritory);
            localStorage.setItem('territory_crm_current_territory', defaultTerritory);
          }
        }
        /* 2026-08-10 (SIWA pending-account incident) · a successful check
           clears any stale denied-reason so a later bounce never shows an
           old cause. */
        try { sessionStorage.removeItem('rv_auth_denied_reason'); } catch { /* noop */ }
        return true;
      } else {
        /* A definitive non-ok answer (expired/revoked session) must also
           kill the offline cache, or the revoked identity would be
           restored on the next network-dead launch. */
        try { localStorage.removeItem('rv_auth_user_cache'); } catch { /* noop */ }
        /* 2026-08-10 (SIWA pending-account incident) · surface WHY the
           session did not stick. A PENDING_APPROVAL account completes the
           whole OAuth dance (tokens mint 201) and then /auth/me rejects with
           "Account is not active" · without this, the rep lands on the
           generic denied screen whose copy says to retry the same provider
           forever (a real founder-hit loop on 08-10). sessionStorage, not
           context state, so the CRM surface and this provider's interface
           stay untouched. Best-effort: an unreadable body keeps the generic
           reason. */
        try {
          const body = (await response.json()) as { message?: string };
          if (typeof body?.message === 'string' && /account is not active/i.test(body.message)) {
            sessionStorage.setItem('rv_auth_denied_reason', 'pending');
          } else {
            sessionStorage.removeItem('rv_auth_denied_reason');
          }
        } catch { /* body unreadable · keep the generic denied reason */ }
        setUser(null);
        setToken(null);
        return false;
      }
    } catch (error) {
      /* Offline restore runs ONLY when no HTTP response arrived (network /
         DNS / airplane mode) - sawResponse fences off parse errors in the
         success path, so a 200-with-bad-body can never masquerade as
         offline. Elevated roles are never restored from a client-writable
         cache: an admin UI resurrected from localStorage in airplane mode
         is an information-exposure surface even with the server re-checking
         every call. 30-day cap so an abandoned device falls back to login. */
      try {
        if (!sawResponse) {
          const raw = localStorage.getItem('rv_auth_user_cache');
          if (raw) {
            const cached = JSON.parse(raw) as { u?: { role?: string; siteRole?: string }; at?: number };
            const elevated = cached?.u && (cached.u.role === 'ADMIN' || cached.u.siteRole === 'ADMIN');
            if (cached?.u && !elevated && typeof cached.at === 'number' && Date.now() - cached.at < 30 * 24 * 3600_000) {
              console.info('Offline · restoring cached session (server re-checks on every call)');
              setUser(cached.u as never);
              setToken('authenticated');
              return true;
            }
          }
        }
      } catch { /* corrupt cache · fall through to logged-out */ }
      console.error('Failed to check auth status:', error);
      setUser(null);
      setToken(null);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [fetchAbilities]);

  useEffect(() => {
    if (DEV_MODE) {
      const devAbility = buildAbility(DEV_ADMIN_RULES);
      setAbility(devAbility);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const storedRegion = localStorage.getItem('territory_crm_current_region');
    const storedTerritory = localStorage.getItem('territory_crm_current_territory');

    checkAuthStatus().then(() => { if (cancelled) return; });

    if (storedRegion) {
      setCurrentRegion(storedRegion);
    }

    if (storedTerritory) {
      setCurrentTerritory(storedTerritory);
    }

    return () => { cancelled = true; };
  }, [checkAuthStatus]);

  // Login just verifies auth via cookies (backend already set the cookie)
  const login = async () => {
    await checkAuthStatus();
  };

  const switchRegion = (region: string) => {
    setCurrentRegion(region);
    localStorage.setItem('territory_crm_current_region', region);
    // When switching regions, reset territory to 'all'
    setCurrentTerritory('all');
    localStorage.setItem('territory_crm_current_territory', 'all');
  };

  const switchTerritory = (zone: string) => {
    setCurrentTerritory(zone);
    localStorage.setItem('territory_crm_current_territory', zone);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        ability,
        currentRegion,
        currentTerritory,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        switchRegion,
        switchTerritory,
        handleApiError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
